// Badge System Configuration and Logic

export const BADGE_CATALOG = [
  // Milestone Badges
  {
    id: 'first_booking',
    name: 'First Steps',
    description: 'Completed your first booking',
    icon: '🎉',
    color: 'from-blue-500 to-cyan-500',
    category: 'milestone',
    criteria: { type: 'booking_count', value: 1 },
    rarity: 'common'
  },
  {
    id: 'booking_5',
    name: 'Getting Started',
    description: 'Completed 5 bookings',
    icon: '🌟',
    color: 'from-green-500 to-emerald-500',
    category: 'milestone',
    criteria: { type: 'booking_count', value: 5 },
    rarity: 'common'
  },
  {
    id: 'booking_10',
    name: 'Rising Star',
    description: 'Completed 10 bookings',
    icon: '⭐',
    color: 'from-yellow-500 to-orange-500',
    category: 'milestone',
    criteria: { type: 'booking_count', value: 10 },
    rarity: 'uncommon'
  },
  {
    id: 'booking_25',
    name: 'Experienced Pro',
    description: 'Completed 25 bookings',
    icon: '💫',
    color: 'from-blue-500 to-sky-500',
    category: 'milestone',
    criteria: { type: 'booking_count', value: 25 },
    rarity: 'rare'
  },
  {
    id: 'booking_50',
    name: 'Veteran Caregiver',
    description: 'Completed 50 bookings',
    icon: '🏆',
    color: 'from-orange-500 to-red-500',
    category: 'milestone',
    criteria: { type: 'booking_count', value: 50 },
    rarity: 'epic'
  },
  {
    id: 'booking_100',
    name: 'Legend',
    description: 'Completed 100 bookings',
    icon: '👑',
    color: 'from-yellow-400 to-yellow-600',
    category: 'milestone',
    criteria: { type: 'booking_count', value: 100 },
    rarity: 'legendary'
  },

  // Quality Badges
  {
    id: 'perfect_5_star',
    name: 'Perfect Record',
    description: 'Maintained 5-star rating with 10+ reviews',
    icon: '✨',
    color: 'from-pink-500 to-rose-500',
    category: 'quality',
    criteria: { type: 'rating_perfect', minReviews: 10, rating: 5.0 },
    rarity: 'epic'
  },
  {
    id: 'top_rated',
    name: 'Top Rated',
    description: 'Average rating of 4.8+ with 20+ reviews',
    icon: '🌟',
    color: 'from-indigo-500 to-blue-500',
    category: 'quality',
    criteria: { type: 'rating_high', minReviews: 20, rating: 4.8 },
    rarity: 'rare'
  },
  {
    id: 'highly_recommended',
    name: 'Highly Recommended',
    description: 'Received 50+ five-star reviews',
    icon: '💝',
    color: 'from-red-500 to-pink-500',
    category: 'quality',
    criteria: { type: 'five_star_count', value: 50 },
    rarity: 'epic'
  },

  // Speed & Reliability Badges
  {
    id: 'quick_responder',
    name: 'Quick Responder',
    description: 'Average response time under 1 hour',
    icon: '⚡',
    color: 'from-yellow-500 to-amber-500',
    category: 'reliability',
    criteria: { type: 'response_time', maxMinutes: 60 },
    rarity: 'uncommon'
  },
  {
    id: 'always_available',
    name: 'Always Available',
    description: 'Accepted 95%+ of booking requests',
    icon: '🎯',
    color: 'from-green-500 to-teal-500',
    category: 'reliability',
    criteria: { type: 'acceptance_rate', rate: 0.95, minRequests: 20 },
    rarity: 'rare'
  },
  {
    id: 'no_cancellations',
    name: 'Rock Solid',
    description: 'Zero cancellations in 25+ bookings',
    icon: '🛡️',
    color: 'from-slate-500 to-zinc-500',
    category: 'reliability',
    criteria: { type: 'zero_cancellations', minBookings: 25 },
    rarity: 'epic'
  },

  // Special Achievement Badges
  {
    id: 'early_adopter',
    name: 'Early Adopter',
    description: 'One of the first users on the platform',
    icon: '🚀',
    color: 'from-blue-600 to-indigo-600',
    category: 'special',
    criteria: { type: 'manual' },
    rarity: 'legendary'
  },
  {
    id: 'community_hero',
    name: 'Community Hero',
    description: 'Helped 10+ first-time pet owners',
    icon: '🦸',
    color: 'from-cyan-500 to-blue-500',
    category: 'special',
    criteria: { type: 'first_time_owners', value: 10 },
    rarity: 'rare'
  },
  {
    id: 'emergency_hero',
    name: 'Emergency Hero',
    description: 'Accepted 5+ last-minute bookings',
    icon: '🚑',
    color: 'from-red-500 to-orange-500',
    category: 'special',
    criteria: { type: 'emergency_bookings', value: 5 },
    rarity: 'epic'
  },
  {
    id: 'long_term_care',
    name: 'Long-Term Expert',
    description: 'Completed 5+ bookings over 30 days',
    icon: '📅',
    color: 'from-violet-500 to-blue-500',
    category: 'special',
    criteria: { type: 'long_bookings', value: 5, minDays: 30 },
    rarity: 'rare'
  },
  {
    id: 'pet_whisperer',
    name: 'Pet Whisperer',
    description: 'Cared for 5+ different pet types',
    icon: '🐾',
    color: 'from-amber-500 to-orange-500',
    category: 'special',
    criteria: { type: 'pet_variety', value: 5 },
    rarity: 'uncommon'
  },
  {
    id: 'super_sitter',
    name: 'Super Sitter',
    description: 'Maintained perfect attendance for 6 months',
    icon: '💪',
    color: 'from-emerald-500 to-green-500',
    category: 'special',
    criteria: { type: 'manual' },
    rarity: 'epic'
  },

  // Platform Contributor Badges
  {
    id: 'verified_professional',
    name: 'Verified Professional',
    description: 'Verified by platform administrators',
    icon: '✓',
    color: 'from-blue-500 to-blue-700',
    category: 'verification',
    criteria: { type: 'manual' },
    rarity: 'rare'
  },
  {
    id: 'certified_trainer',
    name: 'Certified Trainer',
    description: 'Professional pet training certification',
    icon: '🎓',
    color: 'from-indigo-600 to-blue-600',
    category: 'verification',
    criteria: { type: 'manual' },
    rarity: 'epic'
  }
];

// Badge awarding logic
export const checkBadgeEligibility = (userId, userStats, existingBadges = []) => {
  const eligibleBadges = [];
  const alreadyHas = new Set(existingBadges.map(b => b.badgeId));

  BADGE_CATALOG.forEach(badge => {
    // Skip if already has this badge or if manual-only
    if (alreadyHas.has(badge.id) || badge.criteria.type === 'manual') {
      return;
    }

    let isEligible = false;

    switch (badge.criteria.type) {
      case 'booking_count':
        isEligible = (userStats.completedBookings || 0) >= badge.criteria.value;
        break;

      case 'rating_perfect':
        isEligible = (
          (userStats.averageRating || 0) === badge.criteria.rating &&
          (userStats.reviewCount || 0) >= badge.criteria.minReviews
        );
        break;

      case 'rating_high':
        isEligible = (
          (userStats.averageRating || 0) >= badge.criteria.rating &&
          (userStats.reviewCount || 0) >= badge.criteria.minReviews
        );
        break;

      case 'five_star_count':
        isEligible = (userStats.fiveStarReviews || 0) >= badge.criteria.value;
        break;

      case 'response_time':
        isEligible = (userStats.avgResponseTime || Infinity) <= badge.criteria.maxMinutes;
        break;

      case 'acceptance_rate':
        isEligible = (
          (userStats.acceptanceRate || 0) >= badge.criteria.rate &&
          (userStats.totalRequests || 0) >= badge.criteria.minRequests
        );
        break;

      case 'zero_cancellations':
        isEligible = (
          (userStats.cancellations || 0) === 0 &&
          (userStats.completedBookings || 0) >= badge.criteria.minBookings
        );
        break;

      case 'first_time_owners':
        isEligible = (userStats.firstTimeOwners || 0) >= badge.criteria.value;
        break;

      case 'emergency_bookings':
        isEligible = (userStats.emergencyBookings || 0) >= badge.criteria.value;
        break;

      case 'long_bookings':
        isEligible = (userStats.longTermBookings || 0) >= badge.criteria.value;
        break;

      case 'pet_variety':
        isEligible = (userStats.petTypesServed || 0) >= badge.criteria.value;
        break;

      default:
        break;
    }

    if (isEligible) {
      eligibleBadges.push(badge);
    }
  });

  return eligibleBadges;
};

export const getBadgeRarityColor = (rarity) => {
  const colors = {
    common: 'text-gray-600',
    uncommon: 'text-green-600',
    rare: 'text-blue-600',
    epic: 'text-[var(--pf-accent)]',
    legendary: 'text-yellow-600'
  };
  return colors[rarity] || colors.common;
};

export const getBadgeRarityLabel = (rarity) => {
  return rarity.charAt(0).toUpperCase() + rarity.slice(1);
};

